export interface BusinessTemplateSlotPolicy {
  slotCode: string
  componentId: string
  componentType: string
  required: boolean
  removable: boolean
  reorderable: boolean
  replaceable: boolean
  editableProps: string[]
  editableStyles: string[]
}

export interface BusinessTemplateLockPolicy {
  allowAdditionalComponents: boolean
  editablePageFields: string[]
  slots: BusinessTemplateSlotPolicy[]
}

export interface BusinessTemplateAccess {
  readonly canAdd: () => boolean
  readonly canRemove: (componentId: string) => boolean
  readonly canMove: (componentId: string) => boolean
  readonly canReplace: (componentId: string) => boolean
  readonly canEditPage: (field: string) => boolean
  readonly canEditProp: (componentId: string, prop: string) => boolean
  readonly canEditStyle: (componentId: string, style: string) => boolean
}

const policyKeys = ['allowAdditionalComponents', 'editablePageFields', 'slots'] as const
const slotKeys = [
  'slotCode',
  'componentId',
  'componentType',
  'required',
  'removable',
  'reorderable',
  'replaceable',
  'editableProps',
  'editableStyles'
] as const
const stableCodePattern = /^[a-z][a-z0-9_-]{1,63}$/
const fieldNamePattern = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/
const componentTopLevelFields = new Set(['title', 'enabled', 'dataSource', 'navigation'])

const unrestrictedAccess = freezeAccess({
  canAdd: () => true,
  canRemove: () => true,
  canMove: () => true,
  canReplace: () => true,
  canEditPage: () => true,
  canEditProp: () => true,
  canEditStyle: () => true
})

const closedAccess = freezeAccess({
  canAdd: () => false,
  canRemove: () => false,
  canMove: () => false,
  canReplace: () => false,
  canEditPage: () => false,
  canEditProp: () => false,
  canEditStyle: () => false
})

export function createBusinessTemplateAccess(
  rawPolicy: unknown,
  rawSchema: unknown,
  binding: unknown = undefined
): BusinessTemplateAccess {
  if (rawPolicy == null) return binding != null ? closedAccess : unrestrictedAccess

  const policy = parsePolicy(rawPolicy)
  const components = parseSchemaComponents(rawSchema)
  if (!policy || !components || !policyMatchesSchema(policy, components)) return closedAccess

  const pageFields = new Set(policy.editablePageFields)
  const slots = new Map(policy.slots.map(slot => [slot.componentId, slot]))
  const additionalAllowed = policy.allowAdditionalComponents

  return freezeAccess({
    canAdd: () => additionalAllowed,
    canRemove: componentId => {
      const slot = slots.get(componentId)
      return slot ? slot.removable && !slot.required : false
    },
    canMove: componentId => slots.get(componentId)?.reorderable ?? false,
    canReplace: componentId => slots.get(componentId)?.replaceable ?? false,
    canEditPage: field => pageFields.has(field),
    canEditProp: (componentId, prop) => !componentTopLevelFields.has(prop)
      && (slots.get(componentId)?.editableProps.includes(prop) ?? false),
    canEditStyle: (componentId, style) => slots.get(componentId)?.editableStyles.includes(style) ?? false
  })
}

function parsePolicy(rawPolicy: unknown): BusinessTemplateLockPolicy | null {
  let value = rawPolicy
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown
    } catch {
      return null
    }
  }
  if (!hasExactKeys(value, policyKeys)) return null
  if (typeof value.allowAdditionalComponents !== 'boolean') return null
  if (!isFieldArray(value.editablePageFields) || !Array.isArray(value.slots)) return null

  const slots: BusinessTemplateSlotPolicy[] = []
  for (const item of value.slots) {
    if (!hasExactKeys(item, slotKeys)) return null
    if (!isStableCode(item.slotCode)
      || !isStableCode(item.componentId)
      || !isStableCode(item.componentType)
      || typeof item.required !== 'boolean'
      || typeof item.removable !== 'boolean'
      || typeof item.reorderable !== 'boolean'
      || typeof item.replaceable !== 'boolean'
      || !isFieldArray(item.editableProps)
      || !isFieldArray(item.editableStyles)) {
      return null
    }
    slots.push({
      slotCode: item.slotCode.trim(),
      componentId: item.componentId.trim(),
      componentType: item.componentType.trim(),
      required: item.required,
      removable: item.removable,
      reorderable: item.reorderable,
      replaceable: item.replaceable,
      editableProps: [...item.editableProps],
      editableStyles: [...item.editableStyles]
    })
  }

  if (hasDuplicates(value.editablePageFields)
    || hasDuplicates(slots.map(slot => slot.slotCode))
    || hasDuplicates(slots.map(slot => slot.componentId))) {
    return null
  }

  return {
    allowAdditionalComponents: value.allowAdditionalComponents,
    editablePageFields: [...value.editablePageFields],
    slots
  }
}

function parseSchemaComponents(rawSchema: unknown): Map<string, string> | null {
  if (!isRecord(rawSchema) || !Array.isArray(rawSchema.components)) return null
  const components = new Map<string, string>()
  for (const component of rawSchema.components) {
    if (!isRecord(component) || !isNonEmptyString(component.id) || !isNonEmptyString(component.type)) return null
    if (components.has(component.id)) return null
    components.set(component.id, component.type)
  }
  return components
}

function policyMatchesSchema(policy: BusinessTemplateLockPolicy, components: Map<string, string>): boolean {
  return policy.slots.every(slot => components.get(slot.componentId) === slot.componentType)
}

function freezeAccess(access: BusinessTemplateAccess): BusinessTemplateAccess {
  return Object.freeze(access)
}

function hasExactKeys<T extends readonly string[]>(value: unknown, keys: T): value is Record<T[number], unknown> {
  if (!isRecord(value)) return false
  const actualKeys = Object.keys(value)
  return actualKeys.length === keys.length && actualKeys.every(key => keys.includes(key as T[number]))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function isStableCode(value: unknown): value is string {
  return typeof value === 'string' && stableCodePattern.test(value.trim())
}

function isFieldArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string' && fieldNamePattern.test(item))
}

function hasDuplicates(values: string[]): boolean {
  return new Set(values).size !== values.length
}
